import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { ChevronLeft } from "lucide-react";

// Services
import { getMethodApiCall } from "src/services/api";

// Custom Components
import GenericDataLayout from "../../../components/custom/GenericDataLayout";
import GenericTable from "../../../components/custom/GenericTable";

const AttendanceHistoryLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState({ data: [], meta: {} });
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState(moment().subtract(1, "week").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(
    moment().add(1, "day").format("YYYY-MM-DD")
  );
  const getTraineeAttendance = async () => {
    setLoading(true);
    try {
      const storedData = localStorage.getItem("loginedUser");
      let traineeId;
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        traineeId = parsedData?.trainee?.id;
      }

      // Build the base URL
      let url = `/v1/api/trainee/attendance/get?page=${page}&limit=${limit}&startDate=${startDate}&endDate=${endDate}`;

      if (traineeId) {
        url += `&trainee_id=${traineeId}`;
      }

      // Make the API call
      let res = await getMethodApiCall(url);
      if (res?.status === 200) {
        setData(res?.data);
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTraineeAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, startDate, endDate]);

  const handleClearFilters = () => {
      setPage(1);
      setLimit(10);
      setStartDate(moment().subtract(1, "week").format("YYYY-MM-DD"));
      setEndDate(moment().add(1, "day").format("YYYY-MM-DD"));
    };

  const filtersConfig = [
    {
      type: "date",
      label: t("attendance.startDate"),
      value: startDate,
      onChange: (startDate) => {
        setStartDate(startDate); // 1. Update the date
        setPage(1);            // 2. Reset page to 1
      },
      max: endDate,
    },
    {
      type: "date",
      label: t("attendance.endDate"),
      value: endDate,
      onChange: (endDate) => {
        setEndDate(endDate);   // 1. Update the date
        setPage(1);            // 2. Reset page to 1
      },
      min: startDate,
    },
  ];

  const paginationConfig = {
    page: page,
    limit: limit,
    totalRecords: data?.meta?.totalRecords || 0,
    totalPages: data?.meta?.totalPages || 1, // Assuming API returns this, otherwise calculate
    onPageChange: (newPage) => setPage(newPage),
    onLimitChange: (newLimit) => {
      setLimit(newLimit);
      setPage(1); // Reset to page 1 on limit change
    },
  };

  // Columns definition (Replicating table structure for Trainee view)
  const columns = [
    {
      header: t("trainer.table.date"), // Using translation keys from context
      render: (row) => moment(row.date).format("DD/MM/YYYY"),
    },
    {
      header: t('attendance.table.attendance'),
      render: (row) =>
        row?.status,
    },
    {
      header: t("attendance.table.iti"),
      render: (row) =>
        row.iti_master?.iti_name || "-",
    },
    {
      header: t("attendance.table.trade"),
      render: (row) =>
        row?.trade_master?.trade_name || "-",
    },
    {
      header: t("attendance.table.industry"),
      render: (row) => (
        <div className="max-w-[150px] truncate" title={row?.industry_master?.industry_name}>
          {row?.industry_master?.industry_name || "-"}
        </div>
      )
    },
    {
      header: t("itiHome.checkIn"),
      render: (row) =>
        row?.checkin_time ? (
          moment(row?.checkin_time).format("h:mm A")
        ) : (
          <span className="text-red-500 text-xs font-medium">Pending</span>
        ),
    },
    {
      header: t("itiHome.checkOut"),
      render: (row) =>
        row?.checkout_time ? (
          moment(row?.checkout_time).format("h:mm A")
        ) : (
          <span className="text-red-500 text-xs font-medium">Pending</span>
        ),
    },
    {
      header: t("attendance.table.hours_logged"),
      render: (row) => row?.hours_logged || "-",
    }
  ];

  // Custom Action for the Back Button
  const actionsConfig = [
    {
      label: "Back",
      onClick: () => navigate(-1),
      className: "bg-white text-primary border border-primary hover:bg-gray-50",
      icon: ChevronLeft,
    },
  ];

  return (
    <GenericDataLayout
      title={t("attendance.title")}
      filters={filtersConfig}
      pagination={paginationConfig}
      isLoading={loading}
      actions={actionsConfig} 
      onResetFilters={handleClearFilters}
    >
      <GenericTable
        columns={columns}
        data={data?.data || []}
        pagination={paginationConfig}
      />
    </GenericDataLayout>
  );
};

export default AttendanceHistoryLayout;